import { useState, useRef, useEffect } from "react"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { SMOOTH_LINE_API_URL } from "src/lib/constants"

// APIレスポンス（camelCase）
export type PdfItem = {
  key: string
  filename: string
  size: number
  lastModified: string
  datePath: string
  downloadUrl: string | null
}

export type PdfListResponse = {
  files: PdfItem[]
  lastDate: string
}

export const usePdfList = (onNewFileDetected?: () => void) => {
  const [pdfItems, setPdfItems] = useState<PdfItem[]>([])
  const [lastDate, setLastDate] = useState<string>("")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<any>(null)

  const { getWithToken } = useSmoothApi()
  const url = `${SMOOTH_LINE_API_URL}/base_api/smoother/pdfs`

  // useRefでsetInterval内からも参照可能にする
  const topFileKeyRef = useRef<string | null>(null)
  const newItemKeysRef = useRef<Set<string>>(new Set())
  const isInitialLoadRef = useRef(true)
  const onNewFileDetectedRef = useRef(onNewFileDetected)
  const timeoutIdsRef = useRef<Set<NodeJS.Timeout>>(new Set())

  // コールバック関数を最新に保つ
  onNewFileDetectedRef.current = onNewFileDetected

  const fetchPdfList = async () => {
    try {
      const res = await getWithToken(url)
      const data = res.data as PdfListResponse

      // ULIDでソートして一番新しいファイル（一番上）を取得
      const sortedFiles = [...data.files].sort((a, b) => {
        const extractULID = (filename: string) => filename.replace(/\.[^/.]+$/, "")
        const decodeULID = (ulid: string) => new Date(parseInt(ulid.substring(0, 10), 32))

        const ulidA = extractULID(a.filename)
        const ulidB = extractULID(b.filename)
        const dateA = decodeULID(ulidA)
        const dateB = decodeULID(ulidB)
        return dateB.getTime() - dateA.getTime()
      })

      const currentTopFileKey = sortedFiles.length > 0 ? sortedFiles[0].key : null

      // 初回読み込み時は一番上のファイルを記録するだけ
      if (isInitialLoadRef.current) {
        topFileKeyRef.current = currentTopFileKey
        isInitialLoadRef.current = false
      } else {
        // 2回目以降：一番上のファイルが変わったかチェック
        if (currentTopFileKey && topFileKeyRef.current !== currentTopFileKey) {
          // 新しいファイルをハイライト対象に追加
          newItemKeysRef.current.add(currentTopFileKey)

          // 10秒後にハイライトを削除
          const timeoutId = setTimeout(() => {
            newItemKeysRef.current.delete(currentTopFileKey)
            timeoutIdsRef.current.delete(timeoutId)
          }, 10000)
          timeoutIdsRef.current.add(timeoutId)

          // 一番上のファイルを更新
          topFileKeyRef.current = currentTopFileKey

          // 新規ファイル検出のコールバック実行
          if (onNewFileDetectedRef.current) {
            onNewFileDetectedRef.current()
          }
        }
      }

      setPdfItems(sortedFiles)
      setLastDate(data.lastDate)
      setError(null)
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // 初回実行
    fetchPdfList()

    // 5秒毎に実行
    const interval = setInterval(() => {
      fetchPdfList()
    }, 5000)

    return () => {
      clearInterval(interval)
      // 全てのsetTimeoutをクリーンアップ
      timeoutIdsRef.current.forEach((timeoutId) => {
        clearTimeout(timeoutId)
      })
      timeoutIdsRef.current.clear()
    }
  }, [])

  const mutate = () => {
    fetchPdfList()
  }

  const isNewItem = (key: string) => newItemKeysRef.current.has(key)

  return {
    pdfItems,
    lastDate,
    loading,
    error,
    mutate,
    isNewItem,
  }
}
